/**
 * SA-1089: an email body in the Inbox. Email is not Markdown: a leading `#`
 * is a `#`, four spaces are four spaces. The body is rendered as text with
 * its line breaks kept and its http(s) URLs and addresses made clickable;
 * the trailing quoted block a mail client appends under a reply
 * (`emailQuote.splitQuoted`) sits behind "Show quoted text". The Markdown
 * path (`InboxMessageContent`) stays for AI, staff and chat messages.
 */
import { Fragment, useId, useMemo, useState } from 'react'
import { splitQuoted } from './emailQuote'

export const SHOW_QUOTED = 'Show quoted text'
export const HIDE_QUOTED = 'Hide quoted text'

// An http(s) URL, or a bare address. Trailing punctuation a sentence adds
// (`.`, `,`, `)`, `;`, `:`) is left out of the link.
const LINK = /(https?:\/\/[^\s<>"']+|[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/g
const TRAILING = /[.,;:!?)\]]+$/

function linkify(text: string, keyPrefix: string) {
  const out: React.ReactNode[] = []
  let last = 0
  let n = 0
  for (const match of text.matchAll(LINK)) {
    const start = match.index ?? 0
    let raw = match[0]
    const trail = TRAILING.exec(raw)?.[0] ?? ''
    if (trail) raw = raw.slice(0, -trail.length)
    if (start > last) out.push(text.slice(last, start))
    const href = raw.startsWith('http') ? raw : `mailto:${raw}`
    out.push(
      <a key={`${keyPrefix}-${n}`} href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline break-all">
        {raw}
      </a>,
    )
    n += 1
    last = start + raw.length
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

/** The "Show quoted text" button, shared by the text view and the HTML view (SA-1309).
 * `controls` names the element it shows and hides, while that element is in the DOM (SA-1311). */
export function QuotedToggle({ open, onToggle, controls }: { open: boolean; onToggle: () => void; controls?: string }) {
  return (
    <button
      type="button"
      aria-expanded={open}
      aria-controls={controls}
      onClick={onToggle}
      className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
    >
      {open ? HIDE_QUOTED : SHOW_QUOTED}
    </button>
  )
}

export function InboxEmailBody({ text }: { text: string }) {
  const { visible, quoted } = useMemo(() => splitQuoted(text), [text])
  const [open, setOpen] = useState(false)
  const quotedId = useId()
  return (
    <Fragment>
      {visible && (
        <div data-testid="email-body" className="whitespace-pre-wrap wrap-break-word leading-relaxed">
          {linkify(visible, 'v')}
        </div>
      )}
      {quoted && (
        <div className={visible ? 'mt-1.5' : undefined}>
          <QuotedToggle open={open} onToggle={() => setOpen((v) => !v)} controls={open ? quotedId : undefined} />
          {open && (
            <blockquote
              id={quotedId}
              data-testid="email-quoted"
              className="mt-1 border-l-2 border-border pl-2 text-xs text-muted-foreground whitespace-pre-wrap wrap-break-word"
            >
              {linkify(quoted, 'q')}
            </blockquote>
          )}
        </div>
      )}
    </Fragment>
  )
}
